import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  RefreshCw,
  ShoppingBag,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCustomerAuth } from "../context/CustomerAuthContext";
import { apiRequest } from "../services/api";

const CART_KEY = "cart";
const PENDING_ONLINE_ORDER_KEY = "pendingOnlineOrderId";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

function getErrorMessage(error) {
  if (Array.isArray(error?.details?.message)) {
    return error.details.message.join(", ");
  }

  return error?.message || "Unable to verify your payment right now.";
}

export default function CashfreePaymentReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    accessToken,
    isAuthenticated,
    isLoading: isCustomerLoading,
  } = useCustomerAuth();

  const orderId = searchParams.get("orderId");

  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(true);

  const checkPayment = useCallback(async () => {
    if (!accessToken || !orderId) return;

    setIsChecking(true);
    setError("");

    try {
      const result = await apiRequest(
        `/customer-orders/${orderId}/payments/cashfree/status`,
        {
          accessToken,
        },
      );

      setPayment(result);

      if (result?.paymentStatus === "PAID") {
        localStorage.removeItem(CART_KEY);
        localStorage.removeItem(PENDING_ONLINE_ORDER_KEY);
        window.dispatchEvent(new Event("cartUpdated"));
      }

      if (result?.paymentStatus === "FAILED") {
        localStorage.removeItem(PENDING_ONLINE_ORDER_KEY);
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsChecking(false);
    }
  }, [accessToken, orderId]);

  useEffect(() => {
    if (!isCustomerLoading && !isAuthenticated) {
      navigate("/signin", {
        replace: true,
        state: {
          from: `/payment/cashfree/return?${searchParams.toString()}`,
        },
      });
    }
  }, [isAuthenticated, isCustomerLoading, navigate, searchParams]);

  useEffect(() => {
    if (isAuthenticated && orderId) {
      checkPayment();
    }
  }, [checkPayment, isAuthenticated, orderId]);

  if (isCustomerLoading || !isAuthenticated) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  if (!orderId) {
    return (
      <PaymentShell
        icon={<AlertCircle size={34} />}
        iconClassName="bg-red-50 text-red-700"
        eyebrow="Payment unavailable"
        title="We could not identify this order."
        description="Please open My Orders and try the payment again from your pending order."
        primaryAction={{
          label: "View my orders",
          onClick: () => navigate("/userprofile"),
        }}
      />
    );
  }

  if (isChecking) {
    return (
      <PaymentShell
        icon={<RefreshCw size={32} className="animate-spin" />}
        iconClassName="bg-primary-50 text-primary-800"
        eyebrow="Secure verification"
        title="Confirming your payment"
        description="Please wait while CHARMS securely verifies the payment with Cashfree."
      />
    );
  }

  if (error) {
    return (
      <PaymentShell
        icon={<AlertCircle size={34} />}
        iconClassName="bg-red-50 text-red-700"
        eyebrow="Verification needed"
        title="We could not verify the payment yet."
        description={error}
        primaryAction={{
          label: "Try again",
          onClick: checkPayment,
        }}
        secondaryAction={{
          label: "View my orders",
          onClick: () => navigate("/userprofile"),
        }}
      />
    );
  }

  if(payment?.paymentStatus==='PAID'&&payment?.paymentReviewRequired){
    return <PaymentShell icon={<AlertCircle size={34}/>} iconClassName="bg-amber-50 text-amber-700" eyebrow="Payment received" title="Your payment needs order review." description="We received your payment, but cannot automatically confirm this order. Please contact CHARMS with your order reference. Do not pay again." details={[["Order reference",payment.orderNumber],["Payment","Received — confirmation/refund review pending"]]} primaryAction={{label:'View my orders',onClick:()=>navigate('/userprofile')}}/>;
  }
  if (payment?.paymentStatus === "PAID") {
    return (
      <PaymentShell
        icon={<CheckCircle2 size={36} />}
        iconClassName="bg-emerald-50 text-emerald-700"
        eyebrow="Payment successful"
        title="Your order is confirmed."
        description={
          <>
            Thank you for shopping with CHARMS. Your order reference is{" "}
            <span className="font-semibold text-slate-950">
              {payment.orderNumber}
            </span>
            .
          </>
        }
        details={[
          ["Payment", "Paid securely via Cashfree"],
          ["Order total", formatCurrency(payment.totalAmount)],
        ]}
        primaryAction={{
          label: "View my orders",
          onClick: () => navigate("/userprofile"),
        }}
        secondaryAction={{
          label: "Continue shopping",
          onClick: () => navigate("/search/all"),
        }}
      />
    );
  }

  if (payment?.paymentStatus === "FAILED") {
    return (
      <PaymentShell
        icon={<AlertCircle size={34} />}
        iconClassName="bg-red-50 text-red-700"
        eyebrow="Payment was not completed"
        title="Your payment did not go through."
        description="No payment has been accepted for this order. Your cart is still available, so you can try again whenever you are ready."
        primaryAction={{
          label: "Return to checkout",
          onClick: () => navigate("/checkout"),
        }}
        secondaryAction={{
          label: "View my orders",
          onClick: () => navigate("/userprofile"),
        }}
      />
    );
  }

  return (
    <PaymentShell
      icon={<Clock3 size={34} />}
      iconClassName="bg-amber-50 text-amber-700"
      eyebrow="Payment pending"
      title="Your payment is still being processed."
      description="Cashfree has not confirmed a successful payment yet. Do not pay again unless you are certain the previous attempt failed."
      primaryAction={{
        label: "Check payment again",
        onClick: checkPayment,
      }}
      secondaryAction={{
        label: "Return to checkout",
        onClick: () => navigate("/checkout"),
      }}
    />
  );
}

function PaymentShell({
  icon,
  iconClassName,
  eyebrow,
  title,
  description,
  details = [],
  primaryAction,
  secondaryAction,
}) {
  return (
    <main className="flex min-h-[70vh] items-center justify-center bg-[#faf9f7] px-4 py-12">
      <section className="w-full max-w-2xl border border-[#ded8d4] bg-white p-7 text-center shadow-sm sm:p-12">
        <div
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${iconClassName}`}
        >
          {icon}
        </div>

        <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-primary-800">
          {eyebrow}
        </p>

        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
          {title}
        </h1>

        <p className="mx-auto mt-4 max-w-lg text-sm leading-6 text-slate-500">
          {description}
        </p>

        {details.length > 0 ? (
          <div className="mt-8 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2">
            {details.map(([label, value]) => (
              <div key={label} className="bg-white px-5 py-5 text-left">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                  {label}
                </p>

                <p className="mt-2 font-semibold text-slate-950">{value}</p>
              </div>
            ))}
          </div>
        ) : null}

        {primaryAction || secondaryAction ? (
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {primaryAction ? (
              <button
                type="button"
                onClick={primaryAction.onClick}
                className="inline-flex items-center justify-center gap-2 bg-primary-800 px-5 py-4 text-sm font-bold text-white transition hover:bg-primary-950"
              >
                <ShoppingBag size={17} />
                {primaryAction.label}
              </button>
            ) : null}

            {secondaryAction ? (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className="border border-slate-900 px-5 py-4 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-white"
              >
                {secondaryAction.label}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>
    </main>
  );
}
